import { AnnouncementBanner } from 'sharedComponents/Banner/AnnouncementBanner';
import { AccessTime, Check, Close } from '@mui/icons-material';
import { useTheme, Theme } from '@mui/material/styles';
import { ConnectRefreshActiveStatusBannerDataTestIds } from 'data-testids/ConnectDataTestIds';
import { Box, styled, Typography, Collapse } from '@mui/material';
import { PrimaryButton, SecondaryButton } from 'sharedComponents/Buttons';
import { useState, useEffect, useMemo } from 'react';
import { User } from 'types/user';
import preferencesAPI from 'api/preferencesAPI';
import { JobSeekingStatus } from 'types/types';
import { getDaysLeftBeforeActiveJobSeekingStatusExpires } from '../Banner/utils';

interface ConnectRefreshActiveStatusBannerProps {
  user: User;
}

export const ConnectRefreshActiveStatusBanner = ({
  user,
}: ConnectRefreshActiveStatusBannerProps): React.ReactElement | null => {
  const theme = useTheme();
  const [userHasSubmittedResponse, setUserHasSubmittedResponse] = useState(false);
  const [isBannerVisible, setIsBannerVisible] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const daysLeftOfActiveStatus = useMemo(
    () => getDaysLeftBeforeActiveJobSeekingStatusExpires(user),
    [user]
  );

  const handleSubmit = async (
    updatedStatusValue: JobSeekingStatus.OPEN_TO_WORK_AND_MATCHES | JobSeekingStatus.CLOSED_TO_WORK
  ) => {
    try {
      setIsLoading(true);
      const updatedPreference = {
        ...user.preference,
        job_seeking_status: updatedStatusValue,
        job_seeking_status_updated_at: new Date().toISOString(),
      };
      await preferencesAPI.update(user.id, updatedPreference);
      setUserHasSubmittedResponse(true);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (userHasSubmittedResponse) {
      const timer = setTimeout(() => {
        setIsBannerVisible(false);
      }, 4000);
      return () => clearTimeout(timer);
    } else {
      return null;
    }
  }, [userHasSubmittedResponse]);

  return (
    <CollapseWrapper>
      <Collapse in={isBannerVisible}>
        <AnnouncementBanner
          body={
            userHasSubmittedResponse ? (
              <ResponseSubmitted />
            ) : (
              <BannerContent
                handleSubmit={handleSubmit}
                daysLeftOfActiveStatus={daysLeftOfActiveStatus}
                isLoading={isLoading}
              />
            )
          }
          dataTestId={ConnectRefreshActiveStatusBannerDataTestIds.REFRESH_ACTIVE_STATUS_BANNER}
          iconStyle={{ fontSize: 'large', width: '24px', height: '24px' }}
          sx={bannerStyles(theme)}
        />
      </Collapse>
    </CollapseWrapper>
  );
};

const BannerContent = ({ handleSubmit, daysLeftOfActiveStatus, isLoading }) => {
  const theme = useTheme();

  return (
    <BannerContentContainer>
      <TypographyBold variant="body1">
        <AccessTime />
        Your active status will expire in {daysLeftOfActiveStatus} day
        {daysLeftOfActiveStatus !== 1 && 's'} due to inactivity
      </TypographyBold>
      <Typography variant="body1">Are you still actively searching for a new job?</Typography>
      <ButtonsContainer>
        <PrimaryButton
          startIcon={<Check />}
          sx={buttonStyles(theme)}
          onClick={() => handleSubmit(JobSeekingStatus.OPEN_TO_WORK_AND_MATCHES)}
          dataTestId={
            ConnectRefreshActiveStatusBannerDataTestIds.REFRESH_ACTIVE_STATUS_BANNER_YES_BUTTON
          }
          isLoading={isLoading}
          loadingSpinnerSize={20}
        >
          Yes
        </PrimaryButton>
        <SecondaryButton
          startIcon={<Close />}
          sx={buttonStyles(theme)}
          onClick={() => handleSubmit(JobSeekingStatus.CLOSED_TO_WORK)}
          dataTestId={
            ConnectRefreshActiveStatusBannerDataTestIds.REFRESH_ACTIVE_STATUS_BANNER_NO_BUTTON
          }
          isLoading={isLoading}
          loadingSpinnerSize={20}
        >
          No
        </SecondaryButton>
      </ButtonsContainer>
    </BannerContentContainer>
  );
};

const ResponseSubmitted = () => {
  return (
    <TypographyBold variant="body1">
      <Check />
      Thank you! Your status has been successfully updated.
    </TypographyBold>
  );
};

const bannerStyles = (theme: Theme) => ({
  fontSize: theme.typography.h3.fontSize,
  display: 'flex',
  direction: 'row',
  width: '100%',
  padding: '10px 0',
  minHeight: '47px',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  color: theme.palette.warning.dark,
  backgroundColor: theme.palette.yellow.lighter,
  textWrap: 'none',

  [theme.breakpoints.down('mdlg')]: {
    padding: theme.spacing(2, 4),
    textAlign: 'center',
    textWrap: 'wrap',
  },
});

const buttonStyles = (theme: Theme) => ({
  borderRadius: theme.shape.borderRadius * 0.5,
});

const CollapseWrapper = styled(Box)({
  position: 'sticky',
  top: '80px',
  zIndex: 1100,
  width: '100%',
  margin: 0,
  '@media (max-width: 1200px)': {
    top: '64px',
  },
});

const TypographyBold = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMediumBold,
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  gap: theme.spacing(0.5),
}));

const BannerContentContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  [theme.breakpoints.down('mdlg')]: {
    flexDirection: 'column',
    alignItems: 'center',
  },
}));

const ButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  alignItems: 'center',
  marginLeft: theme.spacing(1),
  [theme.breakpoints.down('mdlg')]: {
    marginLeft: 0,
    marginTop: theme.spacing(0.5),
  },
}));
